import React, { useCallback, useEffect, useState } from 'react'

import { useMutation } from '@apollo/client'
import ReactModal from 'react-modal'

import BuildConfig from '$extensionSrc/buildConfig'
import ActionButton, {
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import InteractiveElement from '$extensionSrc/components/InteractiveElement'
import { extensionDashboardUrl } from '$extensionSrc/consts/urlConsts'
import useForm from '$extensionSrc/hooks/useForm'
import CloseIcon from '$extensionSrc/icons/close-icon.svg'
import { UserSettingsModalType } from '$extensionSrc/utils/enums'
import { isNotEmpty } from '$extensionSrc/utils/stringValidator'

import Confirm2faModal from './Confirm2faModal'
import Enroll2faChooseMethodModal from './Enroll2faChooseMethodModal'
import FormTextInput from './FormTextInput'
import { client } from '../apolloClient'
import {
  delete2faSettingsMutation,
  deleteUserMutation,
  updateUserEmailMutation,
  updateUserNameMutation,
} from '../data/query'
import { getExtensionRedirectUrl } from '../utils/urlUtils'

import './SettingsModal.scss'

function DeleteAccountSettingsModal({
  closeModal,
  showPlanSection,
  hasActivePlan,
  planExpiresOn,
  setToastInfo,
}) {
  const defaultValues = {
    delete: '',
  }

  const { getFieldChangeHandler, values } = useForm({
    defaultValues,
    requiredValidations: {
      delete: (x) => x === 'DELETE',
    },
  })

  const [requestDeleteUser, { data: responseData }] = useMutation(
    deleteUserMutation,
    { client },
  )

  const statusCode = responseData?.deleteUser?.statusCode
  const error = responseData?.deleteUser?.error

  useEffect(() => {
    if (statusCode !== 200 && error) {
      setToastInfo({
        show: true,
        text: 'We could not delete your account',
        error: true,
      })
      closeModal()
    } else if (statusCode === 200) {
      // redirect to extension, then show toast
      window.location.assign(
        getExtensionRedirectUrl(extensionDashboardUrl, { deleted: 1 }),
      )
      closeModal()
    }
  }, [statusCode, error, setToastInfo, closeModal])

  const onClickDelete = () => {
    requestDeleteUser({
      variables: {
        id: null, // let query grab id from currentUser
      },
    })
  }

  const content = (
    <div className="settings-modal-content">
      <div className="heading1 bold width-100">
        Are you sure you want to delete your account?
      </div>
      <div className="body1 darkest-gray width-100">
        <p>If you delete your account, you will lose your saved progress.</p>
        {hasActivePlan && planExpiresOn && showPlanSection && (
          <p>
            Your {BuildConfig.TENANT_NAME} plan is active until {planExpiresOn}.
            Plans are pre-paid, so deleting your account will revoke access but
            does not qualify for a refund. For questions, please email us at{' '}
            <a href="mailto:support@blockpartyapp.com">
              support@blockpartyapp.com
            </a>
            .
          </p>
        )}
      </div>
      <div className="form-field-frame">
        <FormTextInput
          handleChange={(value) => getFieldChangeHandler('delete')(value)}
          isSensitive={false}
          name="delete"
          placeholderText="Type DELETE to continue"
          value={values.delete}
        />
      </div>
    </div>
  )

  const footer = (
    <div className="settings-modal-footer">
      <ActionButton
        onClick={() => {
          getFieldChangeHandler('delete')('')
          closeModal()
        }}
        type={ActionButtonType.TERTIARY}
      >
        Cancel
      </ActionButton>
      <ActionButton
        className="delete-button"
        disabled={values.delete !== 'DELETE'}
        onClick={onClickDelete}
        type={ActionButtonType.DANGER}
      >
        Continue deleting
      </ActionButton>
    </div>
  )

  return (
    <div className="settings-modal-body">
      <div className="settings-modal-header">
        <InteractiveElement
          aria-label="Close"
          className="close-button"
          onClick={() => {
            getFieldChangeHandler('delete')('')
            closeModal()
          }}
        >
          <CloseIcon className="close-icon" />
        </InteractiveElement>
      </div>
      {content}
      {footer}
    </div>
  )
}

function Delete2faSettingsModal({ closeModal, currentUserId, setToastInfo }) {
  const defaultValues = {
    password: '',
  }

  const { getFieldChangeHandler, values } = useForm({
    defaultValues,
    requiredValidations: {
      password: (x) => isNotEmpty(x), // Password must be present
    },
  })

  const [requestDelete2fa, { data: responseData }] = useMutation(
    delete2faSettingsMutation,
    { client },
  )

  const statusCode = responseData?.delete2faSettings?.statusCode
  const error = responseData?.delete2faSettings?.error

  useEffect(() => {
    if (statusCode) {
      if (statusCode !== 200 && error) {
        setToastInfo({
          show: true,
          text: '2FA could not be removed',
          error: true,
        })
      } else {
        setToastInfo({
          show: true,
          text: '2FA has been removed',
          error: false,
        })
        setTimeout(() => {
          window.location.reload()
        }, 2000) // Refresh after toast display to clear setting on page
      }
      closeModal()
    }
  }, [statusCode, error, setToastInfo, closeModal])

  const handleConfirmClick = useCallback(() => {
    requestDelete2fa({
      variables: {
        id: currentUserId,
        password: values.password,
      },
    })
  }, [requestDelete2fa, currentUserId, values.password])

  return (
    <div className="settings-modal-body">
      <div className="settings-modal-header">
        <InteractiveElement
          aria-label="Close"
          className="close-button"
          onClick={() => {
            closeModal()
          }}
        >
          <CloseIcon className="close-icon" />
        </InteractiveElement>
      </div>
      <div className="settings-modal-content">
        <div className="heading1 bold width-100">
          Are you sure you want to remove 2FA?
        </div>
        <div className="body1 darkest-gray width-100">
          <p>To proceed, please enter your password.</p>
        </div>
        <div className="form-field-frame">
          <FormTextInput
            handleChange={(value) => getFieldChangeHandler('password')(value)}
            isSensitive
            name="password"
            placeholderText="Enter your password"
            value={values.password}
          />
        </div>
      </div>
      <div className="settings-modal-footer">
        <ActionButton
          onClick={() => {
            closeModal()
          }}
          type={ActionButtonType.TERTIARY}
        >
          Cancel
        </ActionButton>
        <ActionButton
          className="delete-button"
          disabled={false}
          onClick={handleConfirmClick}
          type={ActionButtonType.DANGER}
        >
          Confirm
        </ActionButton>
      </div>
    </div>
  )
}

function ChangeNameSettingsModal({
  closeModal,
  currentUserNameDisplay,
  setCurrentUserNameDisplay,
  setToastInfo,
}) {
  const defaultValues = {
    firstName: currentUserNameDisplay,
  }

  const { getFieldChangeHandler, values } = useForm({
    defaultValues,
    requiredValidations: {},
  })

  const onNameChangeCompleted = () => {
    setCurrentUserNameDisplay(values.firstName)
    setToastInfo({
      show: true,
      text: 'Your name has been updated',
      error: false,
    })
    closeModal()
  }

  const onNameChangeError = () => {
    setToastInfo({
      show: true,
      text: 'We could not update your name',
      error: true,
    })
    closeModal()
  }

  const [requestChangeName] = useMutation(updateUserNameMutation, {
    client,
    onCompleted: onNameChangeCompleted,
    onError: onNameChangeError,
  })

  const onClickSaveName = () => {
    requestChangeName({
      variables: {
        name: values.firstName,
      },
    })
  }

  const content = (
    <div className="settings-modal-content">
      <div className="heading1 bold">Update your name</div>
      <FormTextInput
        handleChange={(value) => getFieldChangeHandler('firstName')(value)}
        isSensitive={false}
        name="firstName"
        placeholderText="What name should we call you? (optional)"
        value={values.firstName}
      />
    </div>
  )

  const footer = (
    <div className="settings-modal-footer">
      <ActionButton
        className="save-button"
        onClick={onClickSaveName}
        type={ActionButtonType.PRIMARY}
      >
        Save
      </ActionButton>
    </div>
  )

  return (
    <div className="settings-modal-body">
      <div className="settings-modal-header">
        <InteractiveElement
          aria-label="Close"
          className="close-button"
          onClick={() => {
            getFieldChangeHandler('firstName')(currentUserNameDisplay)
            closeModal()
          }}
        >
          <CloseIcon className="close-icon" />
        </InteractiveElement>
      </div>
      {content}
      {footer}
    </div>
  )
}

function ChangeEmailSettingsModal({
  closeModal,
  setPrimaryEmail,
  setToastInfo,
  currentEmail,
  validEmailOptions,
}) {
  const [selectedEmail, setSelectedEmail] = useState(currentEmail)

  const [requestChangeEmail] = useMutation(updateUserEmailMutation, {
    client,
    onCompleted: (data) => {
      const newEmail = data?.updateUser?.email
      setPrimaryEmail(newEmail)
      setToastInfo({
        show: true,
        text: 'Your primary email has been updated',
        error: false,
      })
      closeModal()
    },
    onError: () => {
      setToastInfo({
        show: true,
        text: 'We could not update your primary email',
        error: true,
      })
      closeModal()
    },
  })

  const onClickSaveEmail = useCallback(() => {
    requestChangeEmail({
      variables: {
        email: selectedEmail,
      },
    })
  }, [requestChangeEmail, selectedEmail])

  return (
    <div className="settings-modal-body">
      <div className="settings-modal-header">
        <InteractiveElement
          aria-label="Close"
          className="close-button"
          onClick={() => {
            closeModal()
          }}
        >
          <CloseIcon className="close-icon" />
        </InteractiveElement>
      </div>
      <div className="settings-modal-content">
        <div className="heading1 bold">Change your primary email</div>
        <div className="body1 center">
          Your primary email address is where we will contact you. You may use
          any confirmed email address as your primary email.
        </div>
        <label className="body1" htmlFor="email">
          Selected email address:&nbsp;&nbsp;
          <select
            className="email-select"
            id="email"
            value={selectedEmail}
            onChange={(e) => {
              setSelectedEmail(e.target.value)
            }}
          >
            {validEmailOptions.map((x) => (
              <option id={x}>{x}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="settings-modal-footer">
        <ActionButton
          className="save-button"
          onClick={onClickSaveEmail}
          type={ActionButtonType.PRIMARY}
        >
          Save
        </ActionButton>
      </div>
    </div>
  )
}

export default function SettingsModal({ appElement, modalType, modalProps }) {
  const renderModalContent = () => {
    switch (modalType) {
      case UserSettingsModalType.CHANGE_EMAIL:
        return <ChangeEmailSettingsModal {...modalProps} />
      case UserSettingsModalType.CHANGE_NAME:
        return <ChangeNameSettingsModal {...modalProps} />
      case UserSettingsModalType.DELETE_2FA:
        return <Delete2faSettingsModal {...modalProps} />
      case UserSettingsModalType.DELETE_ACCOUNT:
        return <DeleteAccountSettingsModal {...modalProps} />
      case UserSettingsModalType.ENROLL_2FA_APP:
        return <Enroll2faChooseMethodModal {...modalProps} initialTab="app" />
      case UserSettingsModalType.ENROLL_2FA_SMS:
        return <Enroll2faChooseMethodModal {...modalProps} initialTab="sms" />
      case UserSettingsModalType.CONFIRM_2FA_APP:
        return <Confirm2faModal {...modalProps} twofaMethod="app" />
      case UserSettingsModalType.CONFIRM_2FA_SMS:
        return <Confirm2faModal {...modalProps} twofaMethod="sms" />
      default:
    }
    return null
  }

  return (
    <ReactModal
      appElement={appElement}
      className="PrivacyParty SettingsModal"
      isOpen={modalType !== null}
      onRequestClose={modalProps.closeModal}
      overlayClassName="background"
      shouldCloseOnEsc
      shouldCloseOnOverlayClick={false}
    >
      {renderModalContent()}
    </ReactModal>
  )
}
